/**
 * identity-region.js — row 1, left: "where am I".
 *
 * Sidebar toggle · project chip ▾ · `/`. The page switcher follows it in its
 * own slot, so the reading order across row 1 is
 * identity → location → state → destination, which is also the tab order the
 * composite focus model declares (contracts §3).
 *
 * ── What moved, and what did not ──────────────────────────────────────────
 * Every behaviour here is ported from `ui/toolbar.js` unchanged: the chip opens
 * the menu and focuses the rename field, the arrow is the `aria-expanded` host,
 * hover/focus on either warms the templates cache, and the name commits on
 * `change` with the same empty/too-long handling.
 *
 * What changed is construction. The old markup was one `innerHTML` blob with
 * five hand-written `<button>`s and two attribute-sized inline SVGs; every
 * control here comes from `createButton` and every glyph from `icon()`.
 *
 * The ids move WITH their controls — `#pb-project-chip`, `#pb-project-menu-btn`,
 * `#pb-project-name`, `#pb-sidebar-toggle`, `#pb-load-input` and the four menu
 * item ids all keep their names. An id names the control, not its location:
 * `tour-registry.js` walks the user to `#pb-project-chip`, `shell.js` tests
 * `closest('#pb-sidebar-toggle')` on every outside-click, and
 * `command-palette.js` clicks `#pb-load-input` by id. The census parity gate is
 * what proves none of them disappeared.
 *
 * ── One item leaves ───────────────────────────────────────────────────────
 * `share` (Collaborators…) moves to the new Share menu in the destination
 * region — its declared home in the CMD-0 parity matrix. `#pb-share-project-btn`
 * goes with it; nothing is dropped.
 */

import { SAVE_LABELS, PROJECT_LABELS } from '../../shared/ui-vocabulary.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { ToolbarMenu } from '../toolbar-menu.js';
import { prefetchTemplates } from '../../templates/template-api.js';
// `ui/toast.js`, not `shell.js` — a command-bar module that imports the shell
// is unloadable under node and stops being testable at all (gate 5).
import { showToast } from '../toast.js';
import { isComposing } from '../../shared/typing-target.js';

const PROJECT_NAME_MAX_LEN = 80;

export class IdentityRegion {
  /** @param {HTMLElement} container — the `row1.identity` slot */
  constructor(container, { initialAuthPayload = null } = {}) {
    this._container = container;
    this._offs = [];
    this._menuCtrl = null;
    this._nameDraft = null;
    this._nameComposing = false;
    this._committedName = store.getState()?.projectName || 'Untitled';
    this._projectGeneration = store.getEpoch() - store.getRevision();
    this._render();
    this._listen();
    this._syncAuth(initialAuthPayload);
  }

  destroy() {
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    this._menuCtrl?.destroy();
    this._menuCtrl = null;
  }

  /** The project menu closes when a control outside it takes over. */
  closeMenu() {
    this._menuCtrl?.close();
  }

  _render() {
    this._container.classList.add('pb-cb-identity');

    this._sidebarBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon',
      id: 'pb-sidebar-toggle',
      icon: icon('sidebar'),
      ariaLabel: 'Toggle sidebar',
      tooltip: 'Toggle sidebar',
      onClick: () => eventBus.emit('sidebar:toggle')
    });
    this._sidebarBtn.setAttribute('aria-expanded', 'true');

    // ── the chip ──
    const wrap = document.createElement('div');
    wrap.className = 'pb-cb-project-wrap';

    this._chipName = document.createElement('span');
    this._chipName.className = 'pb-project-chip-name';
    this._chipName.id = 'pb-project-chip-name';
    this._chipName.textContent = 'Untitled';

    this._chip = createButton({
      variant: 'bare',
      className: 'pb-project-chip',
      id: 'pb-project-chip',
      // `projects` rather than a new house glyph: the registry already has a
      // symbol meaning "a project", and adding a second one for the same
      // semantic is exactly the drift it exists to stop.
      icon: icon('projects', { size: 'sm' }),
      content: this._chipName,
      ariaLabel: 'Project actions',
      tooltip: 'Rename project'
    });

    this._chipArrow = createButton({
      variant: 'bare',
      className: 'pb-project-chip-arrow',
      id: 'pb-project-menu-btn',
      icon: icon('chevronDown', { size: 'xs' }),
      ariaLabel: 'Project actions',
      tooltip: 'Project actions',
      ariaHaspopup: 'true',
      ariaExpanded: 'false'
    });

    wrap.append(this._chip, this._chipArrow);

    // ── the menu ──
    this._menu = this._buildMenu();

    // The hidden file input the "Open project file…" item and
    // `command-palette.js` both click. It lives outside the menu node because
    // the menu detaches from the DOM while relocated into the popover shell,
    // and a detached <input type=file> cannot open a picker.
    this._loadInput = document.createElement('input');
    this._loadInput.type = 'file';
    this._loadInput.id = 'pb-load-input';
    this._loadInput.accept = '.pb.json,.json,.pb.zip,.zip';
    this._loadInput.hidden = true;

    const crumb = document.createElement('span');
    crumb.className = 'pb-cb-crumb';
    crumb.setAttribute('aria-hidden', 'true');
    crumb.textContent = '/';

    // The menu node lives inert inside the wrap (`.pb-cb-menu { display:none }`)
    // until `ToolbarMenu` relocates it into the popover shell on open, and
    // returns here on close. It has to be IN the document, not just held as a
    // reference: `#pb-project-name` is an authored id the census tracks and two
    // test suites query by id.
    wrap.appendChild(this._menu);

    this._container.append(this._sidebarBtn, wrap, this._loadInput, crumb);
  }

  _buildMenu() {
    const menu = document.createElement('div');
    menu.className = 'pb-cb-menu';
    menu.id = 'pb-project-menu';
    menu.setAttribute('role', 'menu');

    const renameLabel = document.createElement('label');
    renameLabel.className = 'pb-project-menu-rename';
    renameLabel.setAttribute('role', 'none');
    const renameCaption = document.createElement('span');
    renameCaption.textContent = 'Project name';
    this._nameInput = document.createElement('input');
    this._nameInput.type = 'text';
    this._nameInput.className = 'pb-project-name';
    this._nameInput.id = 'pb-project-name';
    this._nameInput.value = this._committedName;
    this._nameInput.placeholder = 'My Website';
    this._nameInput.maxLength = PROJECT_NAME_MAX_LEN;
    this._nameInput.setAttribute('aria-label', 'Project name');
    renameLabel.append(renameCaption, this._nameInput);

    menu.append(renameLabel, this._divider());

    for (const item of [
      // FN-8: a project needs a home. "My Projects" was reachable only from the
      // account menu — under the person rather than beside the project name —
      // and the only route to a fresh start was a row called "Start over…"
      // filed under danger. Both live here now, first, where the project's own
      // name is.
      {
        id: 'pb-new-project-btn',
        'data-project-action': 'new-project',
        label: PROJECT_LABELS.newProject,
        glyph: 'plus'
      },
      {
        id: 'pb-my-projects-btn',
        'data-project-action': 'open-projects',
        label: PROJECT_LABELS.projects,
        glyph: 'projects'
      },
      {
        id: 'pb-template-btn',
        'data-project-action': 'open-templates',
        label: PROJECT_LABELS.templates,
        glyph: 'templates'
      },
      {
        id: 'pb-open-project-btn',
        'data-project-action': 'open-file',
        label: PROJECT_LABELS.openFile,
        glyph: 'load'
      },
      {
        id: 'pb-compose-btn',
        'data-project-action': 'skin-from-url',
        label: PROJECT_LABELS.importBrand,
        glyph: 'skin'
      }
    ]) {
      menu.appendChild(this._menuItem(item));
    }

    // CMD-4: "Save to cloud" and "Save as template" are PROJECT-FILE
    // operations, not site output, and the parity matrix says so. They spent
    // their whole existence in a menu labelled Export, three rows below
    // "Publish site", which is how a project save came to look like a
    // kind of publishing.
    //
    // The auth-gated group is a group because it is REVEALED as one: the shell
    // toggles `#pb-save-cloud-group` on `auth:state`, and hiding one row at a
    // time would leave its heading behind.
    this._cloudGroup = document.createElement('div');
    this._cloudGroup.className = 'pb-cb-menu-group';
    this._cloudGroup.id = 'pb-save-cloud-group';
    this._cloudGroup.setAttribute('role', 'presentation');
    this._cloudGroup.hidden = true;
    this._cloudGroup.append(
      this._divider(),
      this._menuItem({
        id: 'pb-cloud-save-btn',
        'data-save-action': 'cloud',
        label: SAVE_LABELS.cloud,
        glyph: 'cloud'
      })
    );
    menu.appendChild(this._cloudGroup);

    menu.appendChild(this._divider());
    menu.appendChild(
      this._menuItem({
        'data-save-action': 'save-template',
        label: SAVE_LABELS.template,
        glyph: 'template'
      })
    );

    // FN-8: the "Start over…" row is gone. It was the same flow as "New
    // project", named for what it destroys rather than what it produces, and
    // painted as a danger row — so the ordinary act of starting something read
    // as the destructive one. The warning belongs in the confirm dialog, which
    // is where it now is.

    return menu;
  }

  _divider() {
    const el = document.createElement('div');
    el.className = 'pb-cb-menu-divider';
    return el;
  }

  /**
   * The action is declared as a LITERAL `data-*-action` key beside its value,
   * never as `dataset[kind + 'Action']`. The census parity gate reads these out
   * of the source, and an action assembled from fragments is an action that
   * silently stops having a declared destination.
   */
  _menuItem({ id, label, hint, glyph, className, ...attrs }) {
    const btn = createButton({
      variant: 'bare',
      className: ['pb-cb-menu-item', className].filter(Boolean).join(' '),
      id,
      role: 'menuitem',
      icon: icon(glyph, { size: 'sm' }),
      label,
      labelClass: 'pb-cb-menu-item-label'
    });
    if (hint) {
      const copy = document.createElement('span');
      copy.className = 'pb-cb-menu-item-copy';
      copy.appendChild(btn.querySelector('.pb-cb-menu-item-label'));
      const detail = document.createElement('span');
      detail.className = 'pb-cb-menu-item-hint';
      detail.textContent = hint;
      copy.appendChild(detail);
      btn.appendChild(copy);
    }
    for (const [name, value] of Object.entries(attrs)) btn.setAttribute(name, value);
    return btn;
  }

  _listen() {
    this._menuCtrl = new ToolbarMenu(this._chipArrow, this._menu, {
      align: 'left',
      width: 260,
      // Anchor under the whole chip, while the arrow stays the aria-expanded
      // host and the focus-return target.
      getAnchorRect: () => this._chip.parentElement.getBoundingClientRect(),
      onOpen: () => eventBus.emit('toolbar:menu-opened')
    });

    this._chip.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      // The popover's initial-focus path already focuses the name input.
      // A second animation-frame focus/select can overwrite typing begun
      // after the menu opened, or move a caret the user just positioned.
      this._menuCtrl.open();
    });

    // Hover prefetch: warm the templates cache before the click. pointerenter
    // covers mouse + pen + touch; focus catches keyboard-only flows.
    const warm = () => prefetchTemplates();
    for (const el of [this._chip, this._chipArrow, this._menu.querySelector('#pb-template-btn')]) {
      el?.addEventListener('pointerenter', warm, { passive: true });
      el?.addEventListener('focus', warm, true);
    }

    this._menu.addEventListener('click', (e) => {
      const action = e.target.closest('[data-project-action]')?.dataset.projectAction;
      if (!action) return;
      if (action === 'open-templates') eventBus.emit('toolbar:open-templates');
      else if (action === 'open-file') this._loadInput.click();
      else if (action === 'skin-from-url') eventBus.emit('toolbar:skin-from-url');
      else if (action === 'open-projects') eventBus.emit('toolbar:open-projects');
      // The event name is unchanged: this is the same flow, and shell.js plus
      // save-load-tests pin the behaviour rather than the label.
      else if (action === 'new-project') eventBus.emit('toolbar:start-over');
    });

    this._menu.addEventListener('click', (e) => {
      const action = e.target.closest('[data-save-action]')?.dataset.saveAction;
      if (action === 'cloud') eventBus.emit('toolbar:cloud-save');
      else if (action === 'save-template') eventBus.emit('toolbar:save-template');
    });

    // The auth-gated group. `#pb-save-cloud-group` is revealed only for a REAL
    // account — the dev force-auth flag may show the account chip and must
    // never reveal cloud project endpoints.
    this._offs.push(eventBus.on('auth:state', (payload) => this._syncAuth(payload)));

    this._loadInput.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) eventBus.emit('toolbar:load-file', { file });
      e.target.value = ''; // reset so the same file can be re-selected
    });

    this._nameInput.addEventListener('focus', () => this._beginNameDraft());
    this._nameInput.addEventListener('input', () => this._beginNameDraft());
    this._nameInput.addEventListener('compositionstart', () => {
      this._beginNameDraft();
      this._nameComposing = true;
    });
    this._nameInput.addEventListener('compositionend', () => {
      this._nameComposing = false;
      if (document.activeElement !== this._nameInput) this._commitName();
    });
    this._nameInput.addEventListener('change', () => {
      if (!this._nameComposing) this._commitName();
    });
    this._nameInput.addEventListener('blur', () => {
      if (!this._nameComposing) this._commitName();
    });

    this._nameInput.addEventListener('keydown', (e) => {
      if (this._nameComposing || isComposing(e)) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        this._commitName();
        this._nameInput.blur();
        this._menuCtrl?.close();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this._nameDraft = null;
        this._nameInput.value = this._committedName;
        this._nameInput.blur();
        this._menuCtrl?.close();
      }
    });

    this._offs.push(
      eventBus.on('sidebar:state-changed', ({ open }) =>
        this._sidebarBtn.setAttribute('aria-expanded', String(open))
      )
    );
    this._offs.push(
      eventBus.on('state-changed', (state) => {
        if (!state) return;
        // Mutations (including transaction aborts) increase epoch and revision
        // together. init() alone advances epoch while resetting revision, so
        // this distinguishes a replacement from a new object on rollback.
        const generation = store.getEpoch() - store.getRevision();
        const replaced = generation !== this._projectGeneration;
        this._projectGeneration = generation;
        this._committedName = state.projectName || 'Untitled';
        const editing = document.activeElement === this._nameInput || this._nameComposing;
        if (replaced || !editing) {
          this._nameDraft = null;
          this._nameComposing = false;
          this._nameInput.value = this._committedName;
          if (replaced && editing) showToast('Project changed', 'info');
        }
        this._syncChip(this._committedName);
      })
    );

    this._syncChip(store.getState()?.projectName || 'Untitled');
  }

  _syncAuth(payload) {
    const isRealAccount = Boolean(
      payload && payload.sub && payload.authMode !== 'single-user' && payload.sub !== 'admin'
    );
    this._cloudGroup.hidden = !isRealAccount;
  }

  _beginNameDraft() {
    this._nameDraft ??= { initialName: this._committedName };
  }

  _commitName() {
    const oldName = store.getState().projectName;
    const unchangedDraft = this._nameDraft && this._nameInput.value === this._nameDraft.initialName;
    let name = unchangedDraft ? this._committedName : this._nameInput.value.trim();
    this._nameDraft = null;
    if (!name) {
      this._nameInput.value = this._committedName;
      showToast('Project name cannot be empty', 'error');
      return;
    }
    if (name.length > PROJECT_NAME_MAX_LEN) {
      name = name.slice(0, PROJECT_NAME_MAX_LEN).trim();
      showToast(`Project name limited to ${PROJECT_NAME_MAX_LEN} characters`, 'info');
    }
    if (oldName !== name && !store.update('projectName', name, 'Rename project')) {
      // The store emits the refusal for the shell to explain. A blocked write
      // must not become the field's committed value or a success toast.
      this._committedName = store.getState().projectName || 'Untitled';
      this._nameInput.value = this._committedName;
      this._syncChip(this._committedName);
      return;
    }
    this._committedName = name;
    this._nameInput.value = name;
    this._syncChip(name);
    if (oldName !== name) showToast(`Project renamed to "${name}"`);
  }

  _syncChip(name) {
    const label = String(name || 'Untitled').trim() || 'Untitled';
    this._chipName.textContent = label;
    // The chip is the rename affordance and the menu trigger at once, so the
    // project name has to reach its accessible name — the visible label is
    // clipped to 150px and disappears entirely below 1023px.
    this._chip.setAttribute('aria-label', `Project: ${label}. Project actions`);
  }
}
